import { t } from "ttag";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { IZoneMap } from "../../models/Zones";

export const SERVICE_UNIT_TYPE = 3;
export const CONTROL_UNIT_TYPE = 1;
export const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'];
export const EXPECTED_HEADERS = ['Unit Name', 'Unit Address', 'Tenant Name'];
export const SAMPLE_ROWS: string[][] = [
  ['Unit 1', '001', 'Tenant 1'],
  ['Unit 2', '002', 'Tenant 1'],
  ['Unit 3', '003', 'Tenant 2'],
];

export const getUnitType = (isControlBasedPPD: boolean): number =>
  isControlBasedPPD ? CONTROL_UNIT_TYPE : SERVICE_UNIT_TYPE;

export type IResultCategory = 'mapping' | 'reassignment' | 'skipped' | 'error';

export interface IResultMessage {
  rowNumber: number;
  type: 'good' | 'warning' | 'error';
  category: IResultCategory;
  message: string;
}

export const categoryFromType = (type: IResultMessage['type']): IResultCategory =>
  type === 'good' ? 'mapping' : type === 'warning' ? 'skipped' : 'error';

const PLURAL_LABELS: Record<IResultCategory, [string, string]> = {
  mapping: ['new mapping', 'new mappings'],
  reassignment: ['reassignment', 'reassignments'],
  skipped: ['skipped row', 'skipped rows'],
  error: ['error', 'errors'],
};

export const summarizeByCategory = (rows: IResultMessage[]): string => {
  const counts = rows.reduce((acc, row) => {
    acc[row.category] = (acc[row.category] || 0) + 1;
    return acc;
  }, {} as Record<IResultCategory, number>);

  return (['mapping', 'reassignment', 'skipped', 'error'] as IResultCategory[])
    .filter((category) => counts[category])
    .map((category) => {
      const [singular, pluralWord] = PLURAL_LABELS[category];
      const count = counts[category];
      return `${count} ${count === 1 ? singular : pluralWord}`;
    })
    .join(', ');
};

const toCsvCell = (value: string | number): string => {
  const str = String(value ?? '');
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const buildCsv = (headers: string[], rows: (string | number)[][]): string => {
  const lines = [headers.map(toCsvCell).join(',')];
  rows.forEach((row) => lines.push(row.map(toCsvCell).join(',')));
  return lines.join('\r\n');
};

const UTF8_BOM = '﻿';

export const downloadSampleTemplate = () => {
  const csv = buildCsv(EXPECTED_HEADERS, SAMPLE_ROWS);
  const blob = new Blob([UTF8_BOM + csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, 'tenant-list-sample-template.csv');
};

export const exportTenantListCsv = ({
  allUnits,
  tenants,
  siteId,
  siteName,
  unitType,
}: {
  allUnits: Record<string, any>;
  tenants: IZoneMap;
  siteId: string;
  siteName: string;
  unitType: number;
}) => {
  const unitIdToTenantName: Record<string, string> = {};
  Object.values(tenants).forEach((zone: any) => {
    (zone.units || []).forEach((unitId: string) => {
      unitIdToTenantName[unitId] = zone.name || '';
    });
  });

  const rows = Object.values(allUnits)
    .filter((unit: any) => unit.site === siteId && unit.type === unitType)
    .map((unit: any) => [unit.name || '', unit.address || '', unitIdToTenantName[unit.id] || ''])
    .sort((a, b) => String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true }));

  const csv = buildCsv(EXPECTED_HEADERS, rows);
  const blob = new Blob([UTF8_BOM + csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${siteName || 'site'}-tenant-list.csv`);
};

const parseCsvText = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\r') {
      // skip, \n handled below closes the row
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }

  return rows
    .map((cells) => cells.map((cell) => cell.trim()))
    .filter((cells) => cells.some((cell) => cell !== ''));
};

const parseXlsxFile = async (file: File): Promise<string[][]> => {
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const sheet = workbook.worksheets[0];
  const rows: string[][] = [];

  sheet?.eachRow((excelRow) => {
    const values = (excelRow.values as any[]).slice(1);
    const cells = values.map((value) => (value === null || value === undefined ? '' : String(value).trim()));
    if (cells.some((cell) => cell !== '')) {
      rows.push(cells);
    }
  });

  return rows;
};

export const parseFile = async (file: File): Promise<string[][]> => {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  if (extension === '.xlsx') {
    return parseXlsxFile(file);
  }
  const text = await file.text();
  return parseCsvText(text);
};

export const validateStructure = (rows: string[][]): string[][] | null => {
  if (rows.length < 2) {
    return null;
  }
  const headerRow = rows[0].map((cell) => cell.trim().toLowerCase());
  const expected = EXPECTED_HEADERS.map((header) => header.toLowerCase());
  if (headerRow.length !== 3 || expected.some((header, index) => headerRow[index] !== header)) {
    return null;
  }
  return rows.slice(1);
};

export interface IRowContext {
  zonesById: Record<string, any>;
  zoneIdByName: Record<string, string>;
  unitZoneId: Record<string, string>;
  matchingUnitsByName: Record<string, any>;
  seenUnitIds: Set<string>;
}

export const buildRowContext = (
  tenants: IZoneMap,
  allUnits: Record<string, any>,
  siteId: string,
  unitType: number,
): IRowContext => {
  const zonesById: Record<string, any> = {};
  const zoneIdByName: Record<string, string> = {};
  const unitZoneId: Record<string, string> = {};
  Object.values(tenants).forEach((zone: any) => {
    zonesById[zone.id] = zone;
    zoneIdByName[(zone.name || '').trim().toLowerCase()] = zone.id;
    (zone.units || []).forEach((unitId: string) => {
      unitZoneId[unitId] = zone.id;
    });
  });

  const matchingUnitsByName: Record<string, any> = {};
  Object.values(allUnits).forEach((unit: any) => {
    if (unit.site === siteId && unit.type === unitType) {
      matchingUnitsByName[(unit.name || '').trim()] = unit;
    }
  });

  return { zonesById, zoneIdByName, unitZoneId, matchingUnitsByName, seenUnitIds: new Set() };
};

export type IRowOutcome =
  | { kind: 'warning'; message: string }
  | { kind: 'error'; message: string }
  | {
    kind: 'ready';
    unit: any;
    unitName: string;
    tenantName: string;
    tenantKey: string;
    targetZoneId?: string;
    currentZoneId?: string;
    currentTenantName?: string;
  };

export const classifyRow = (rowNumber: number, dataRow: string[], ctx: IRowContext): IRowOutcome => {
  const [rawUnitName = '', rawUnitAddress = '', rawTenantName = ''] = dataRow;
  const unitName = rawUnitName.trim();
  const unitAddress = rawUnitAddress.trim();
  const tenantName = rawTenantName.trim();

  if (!tenantName) {
    return { kind: 'warning', message: t`Row #${rowNumber} - No actions required` };
  }

  if (!unitName) {
    return { kind: 'error', message: t`Row #${rowNumber} — Cannot import: Missing unit name` };
  }

  const unit = ctx.matchingUnitsByName[unitName];
  if (!unit) {
    return { kind: 'error', message: t`Row #${rowNumber} — Cannot import: Unit not found` };
  }

  if (ctx.seenUnitIds.has(unit.id)) {
    return { kind: 'error', message: t`Row #${rowNumber} — Cannot import: Duplicate unit in file` };
  }
  ctx.seenUnitIds.add(unit.id);

  if (!unitAddress) {
    return { kind: 'error', message: t`Row #${rowNumber} — Cannot import: Missing unit address` };
  }

  if ((unit.address || '').trim() !== unitAddress) {
    return { kind: 'error', message: t`Row #${rowNumber} — Cannot import: Unit address mismatch` };
  }

  const tenantKey = tenantName.toLowerCase();
  const currentZoneId = ctx.unitZoneId[unit.id];

  return {
    kind: 'ready',
    unit,
    unitName,
    tenantName,
    tenantKey,
    targetZoneId: ctx.zoneIdByName[tenantKey],
    currentZoneId,
    currentTenantName: currentZoneId ? ctx.zonesById[currentZoneId]?.name : undefined,
  };
};

// Dry-run preview: walks the rows the same way the real import will, so a
// tenant created earlier in the file is reflected in later rows' preview —
// without calling any create/assign APIs.
export const buildPreviewMessages = (dataRows: string[][], ctx: IRowContext): IResultMessage[] => {
  const messages: IResultMessage[] = [];

  dataRows.forEach((dataRow, i) => {
    const rowNumber = i + 1;
    const outcome = classifyRow(rowNumber, dataRow, ctx);

    if (outcome.kind !== 'ready') {
      messages.push({ rowNumber, type: outcome.kind, category: categoryFromType(outcome.kind), message: outcome.message });
      return;
    }

    if (!outcome.targetZoneId) {
      messages.push({
        rowNumber,
        type: 'good',
        category: 'mapping',
        message: t`Row #${rowNumber} — Will create tenant "${outcome.tenantName}" and assign unit ${outcome.unitName}`,
      });
      const placeholderId = `__pending__${outcome.tenantKey}`;
      ctx.zoneIdByName[outcome.tenantKey] = placeholderId;
      ctx.unitZoneId[outcome.unit.id] = placeholderId;
      ctx.zonesById[placeholderId] = { name: outcome.tenantName };
    } else if (outcome.currentZoneId && outcome.currentZoneId !== outcome.targetZoneId) {
      messages.push({
        rowNumber,
        type: 'warning',
        category: 'reassignment',
        message: t`Row #${rowNumber} — Unit ${outcome.unitName} is currently assigned to "${outcome.currentTenantName}". Importing will reassign it to "${outcome.tenantName}"`,
      });
      ctx.unitZoneId[outcome.unit.id] = outcome.targetZoneId;
    } else {
      messages.push({
        rowNumber,
        type: 'good',
        category: 'mapping',
        message: t`Row #${rowNumber} — Will assign unit ${outcome.unitName} to tenant "${outcome.tenantName}"`,
      });
      ctx.unitZoneId[outcome.unit.id] = outcome.targetZoneId;
    }
  });

  return messages;
};
